import { ErrorMessage } from '@hookform/error-message';
import { clstx } from '@payaca/utilities/cvaMerge';
import {
  ComponentProps,
  FC,
  FunctionComponent,
  MouseEventHandler,
  PropsWithChildren,
  useContext,
  useId,
} from 'react';
import {
  FieldError,
  get,
  useController,
  useFormContext,
} from 'react-hook-form';
import Button from '../Button/Button';
import { FieldContext, FieldValidationState } from './Context';
import { FieldError as FieldErrorMessages } from './FieldError';
import FieldLegacy from './FieldLegacy';

/**
 * Field wrapper providing label, helper text, and validation error display.
 *
 * Children should be ordered: Label → HelperText → Input → (other controls).
 * Error messages are rendered automatically at the bottom.
 *
 * @example
 *  <Field name="fieldName">
 *    <Field.Label>Field label</Field.Label>
 *    <Field.HelperText>Guidance for the user</Field.HelperText>
 *    <Select options={[{ label: "option 1", value: 1 }]} value={1} />
 *  </Field>
 */

export type FieldProps = {
  id?: string;
  name: string;
  className?: string;
};

/**
 * A compound component for form fields, providing label, helper text, and
 * validation error display.
 *
 * ## When to use
 *
 * - Any form input that needs a label, helper text, or validation error display
 *
 * ## When to choose another component
 *
 * - **Outside a react-hook-form context** — use `Field.Legacy`. It accepts a
 *   `validationState` prop directly.
 *
 * ## Composition rules
 *
 * Children should follow this order:
 *
 * 1. `Field.Label` - The field label
 * 2. `Field.HelperText` - Optional guidance text, shown below the label and
 *    above the input
 * 3. Input component (e.g. `Input`, `Select`, `Textarea`)
 *
 * Error messages are rendered automatically at the bottom by the Field wrapper.
 *
 * When a field has helper text, place `Field.Optional` or `Field.ClearButton`
 * in a flex row alongside the helper text:
 *
 * ```tsx
 * <Field name="fieldName">
 *   <Field.Label>Label</Field.Label>
 *   <div className="flex items-center justify-between">
 *     <Field.HelperText>Guidance for the user</Field.HelperText>
 *     <Field.Optional />
 *   </div>
 *   <Input />
 * </Field>
 * ```
 *
 * When there is no helper text, place them alongside the label instead:
 *
 * ```tsx
 * <Field name="fieldName">
 *   <div className="flex items-center justify-between">
 *     <Field.Label>Label</Field.Label>
 *     <Field.ClearButton clearValue={null} />
 *   </div>
 *   <Input />
 * </Field>
 * ```
 */
const Field: FC<PropsWithChildren<FieldProps>> = (props) => {
  const { id: customId, name, children, className } = props;

  const generatedId = useId();
  const uId = customId || generatedId;

  /**
   * react hook form context
   */
  const {
    formState: { errors },
  } = useFormContext();

  /**
   * build legacy style `FieldValidationState` object
   */
  // eslint-disable-next-line typescript-eslint/no-redundant-type-constituents -- FieldError resolves to error type in some TS contexts
  const fieldError = get(errors, name);
  const validationState: FieldValidationState | undefined =
    fieldError && !Array.isArray(fieldError)
      ? {
          isValid: false,
          validationMessages: [
            fieldError.message ||
              fieldError.root?.message ||
              `Unknown Error: ${fieldError.type}`,
          ],
        }
      : undefined;

  return (
    <FieldContext.Provider
      value={{
        id: uId,
        name,
        validationState,
      }}
    >
      <div className={clstx('typography:prose space-y-2', className)}>
        {children}

        <ErrorMessage
          name={name}
          errors={errors}
          render={({ message }) => {
            return <FieldErrorMessages messages={[message]} />;
          }}
        />
      </div>
    </FieldContext.Provider>
  );
};

const Label: FC<Omit<ComponentProps<'label'>, 'htmlFor'>> = ({
  children,
  className,
  ...rest
}) => {
  const { id } = useContext(FieldContext);

  return (
    <label htmlFor={id} className={clstx('block', className)} {...rest}>
      {children}
    </label>
  );
};

const Helper: FunctionComponent<PropsWithChildren> = ({ children }) => {
  return <p className="supporting-body text-gray-500">{children}</p>;
};

const Optional: FC<{ className?: string }> = ({ className }) => (
  <p className={clstx('supporting-body text-gray-500', className)}>Optional</p>
);

const ClearButton: FC<
  PropsWithChildren<{ clearValue: any } & ComponentProps<'button'>>
> = (props) => {
  const { clearValue, onClick, children, ...rest } = props;

  const { name } = useContext(FieldContext);

  if (!name) {
    throw new Error('ClearButton must be used within a Field with a name.');
  }

  const { control } = useFormContext();

  const {
    field: { onChange },
  } = useController({
    name,
    control,
  });

  const handleClear: MouseEventHandler<HTMLButtonElement> = (e) => {
    onChange(clearValue);
    onClick?.(e);
  };

  return (
    <Button variant="link" linkColour="dark" onClick={handleClear} {...rest}>
      {children || 'Clear'}
    </Button>
  );
};

export default Object.assign(Field, {
  Legacy: FieldLegacy,
  Label,
  HelperText: Helper,
  ClearButton,
  Optional,
});
